import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import {
    calculationTypeLabels,
    cellClass,
    columnLabels,
    formatCatalogCostFormula,
    getGroupColSpan,
    isGroupRow,
} from '../catalogOtherAndLimitedHelpers';
import { TableRowActionsButton } from '../components/TableRowActionsButton';
import type { TCatalogOtherAndLimitedId, TCatalogOtherAndLimitedItem, TCatalogOtherAndLimitedRow } from '../types';
import { CatalogOtherAndLimitedTab } from '../types';

type TUseCatalogOtherAndLimitedColumnsProps = {
    tab: CatalogOtherAndLimitedTab;
    canEdit: boolean;
    canDelete: boolean;
    items: TCatalogOtherAndLimitedItem[];
    onEdit: (id: TCatalogOtherAndLimitedId) => () => void;
    onRemove: (id: number) => () => void;
    onNameFilterChange: (values: string[]) => void;
};

export const useCatalogOtherAndLimitedColumns = ({
    tab,
    canEdit,
    canDelete,
    items,
    onEdit,
    onRemove,
    onNameFilterChange,
}: TUseCatalogOtherAndLimitedColumnsProps) => {
    const labels = columnLabels[tab];
    const isRowActionsDisabled = !canEdit && !canDelete;
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;
    const groupColSpan = useMemo(() => getGroupColSpan(tab), [tab]);

    const nameFilterItems = useMemo(
        () =>
            Array.from(new Set(items.map((item) => item.name))).map((name) => ({
                name,
            })),
        [items],
    );

    const changeFilterHandler = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => {
            if (fieldName === 'name') {
                onNameFilterChange(checkedValues);
            }
        },
        [onNameFilterChange],
    );

    const columns = useMemo<ColDef<TCatalogOtherAndLimitedRow>[]>(
        () => [
            {
                field: 'rowActions' as any,
                headerName: '',
                pinned: true,
                lockPosition: 'left',
                resizable: false,
                sortable: false,
                width: 48,
                cellClass,
                cellRenderer: ({ data }: { data?: TCatalogOtherAndLimitedRow }) =>
                    !data || isGroupRow(data) ? null : (
                        <TableRowActionsButton
                            id={data.id}
                            onEdit={onEdit}
                            onRemove={onRemove}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            disabled={isRowActionsDisabled}
                        />
                    ),
            },
            {
                field: 'name',
                headerName: labels.name,
                flex: 2,
                minWidth: 320,
                cellClass,
                colSpan: groupColSpan,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    changeFilterHandler,
                    filterItems: nameFilterItems,
                    dataAccessor: `${labels.tableUniqueId}NameFilter`,
                    customRequestId: `${labels.tableUniqueId}NameFilter`,
                    withSearch: true,
                    isClientSideSearch: true,
                    hideTableHeader: true,
                    childFieldAccessor: 'name',
                    columns: [
                        {
                            headerName: '',
                            field: 'name',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                },
            },
            ...(isOthersTab
                ? [
                      {
                          field: 'calculatingType',
                          headerName: 'Способ расчета',
                          flex: 1,
                          minWidth: 140,
                          cellClass,
                          valueGetter: ({ data }: { data?: TCatalogOtherAndLimitedRow }) => {
                              if (!data || isGroupRow(data)) {
                                  return '';
                              }

                              return calculationTypeLabels[data.calculatingType] ?? '';
                          },
                      } as ColDef<TCatalogOtherAndLimitedRow>,
                  ]
                : []),
            {
                field: 'typeName',
                headerName: labels.typeName,
                flex: 1,
                minWidth: 200,
                cellClass,
            },
            {
                field: 'formula',
                headerName: 'Формула',
                flex: 1,
                minWidth: 220,
                cellClass,
                valueGetter: ({ data }) => (data && !isGroupRow(data) ? formatCatalogCostFormula(data.formula) : ''),
            },
        ],
        [
            canDelete,
            canEdit,
            changeFilterHandler,
            groupColSpan,
            isOthersTab,
            isRowActionsDisabled,
            labels.name,
            labels.tableUniqueId,
            labels.typeName,
            nameFilterItems,
            onEdit,
            onRemove,
        ],
    );

    const [selection, , , setSelection] = useSelection(labels.tableUniqueId);
    const [pinnedItems, , , setPinned] = usePin(labels.tableUniqueId);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column) => {
                const newColumn = { ...column } as ColDef & { disabled: boolean };

                if (newColumn.field === 'rowActions') {
                    return acc;
                }

                if (newColumn.field === 'name') {
                    newColumn.disabled = true;
                }

                if (pinnedItems[getColumnId(column)] || newColumn.field === 'rowActions') {
                    newColumn.pinned = true;
                }

                acc.push(newColumn);

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return { columns: fullSetColumns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm };
};
